import { isIsoCalendarDate } from "../portfolio/schema";
import {
  MAX_PUBLIC_LOGO_KEY_LENGTH,
  MAX_PUBLIC_PET_NAME_LENGTH,
  MAX_PUBLIC_RENDERER_VERSION_LENGTH,
  MAX_PUBLIC_ROOM_HISTORY_POINTS,
  MAX_PUBLIC_ROOM_PETS,
  MAX_PUBLIC_ROOM_SNAPSHOT_BYTES,
  PUBLIC_ALLOCATION_BUCKET_VALUES,
  PUBLIC_PERFORMANCE_DIRECTION_VALUES,
  PUBLIC_PET_HAT_VALUES,
  PUBLIC_PET_PALETTE_VALUES,
  PUBLIC_PET_PATTERN_VALUES,
  PUBLIC_ROOM_PET_ID_PATTERN,
  PUBLIC_ROOM_SEED_PATTERN,
  PUBLIC_ROOM_SNAPSHOT_SCHEMA_VERSION,
  PUBLIC_ROOM_TICKER_PATTERN,
  PUBLIC_ROOM_WEATHER_VALUES,
  SHARE_ALLOCATION_VISIBILITY_VALUES,
  SHARE_HISTORY_VISIBILITY_VALUES,
  SHARE_IDENTITY_VISIBILITY_VALUES,
  SHARE_PERFORMANCE_VISIBILITY_VALUES,
} from "./constants";
import { neutralAppearanceForPublicPetV1 } from "./privacy";
import type {
  PublicAllocationBucketV1,
  PublicPerformanceSignalV1,
  PublicPetAppearanceV1,
  PublicRoomHistoryPetV1,
  PublicRoomHistoryPointV1,
  PublicRoomHistoryV1,
  PublicRoomPetV1,
  PublicRoomSnapshotV1,
  PublicRoomStateV1,
  ShareHistoryVisibilityV1,
  ShareVisibilityPolicyV1,
} from "./schema";

const MAX_VALIDATION_ISSUES = 32;
const OBJECT_PROTOTYPE = Object.prototype;
const SNAPSHOT_KEYS = [
  "schemaVersion",
  "rendererVersion",
  "publicSeed",
  "visibility",
  "room",
  "pets",
  "history",
] as const;
const POLICY_KEYS = [
  "identity",
  "performance",
  "allocation",
  "history",
] as const;
const ROOM_KEYS = ["weather"] as const;
const PET_KEYS = [
  "id",
  "appearance",
  "ticker",
  "name",
  "logoKey",
  "performance",
  "allocationBucket",
] as const;
const APPEARANCE_KEYS = ["paletteId", "patternId", "hatId"] as const;
const PERFORMANCE_KEYS = ["direction", "roundedPercent"] as const;
const HISTORY_KEYS = ["range", "points"] as const;
const HISTORY_POINT_KEYS = ["marketDate", "room", "pets"] as const;
const HISTORY_PET_KEYS = [
  "id",
  "performance",
  "allocationBucket",
] as const;

export type PublicRoomValidationIssueCode =
  | "invalid_type"
  | "invalid_value"
  | "unknown_key"
  | "unsupported_version"
  | "too_many_items"
  | "duplicate_value"
  | "privacy_policy_mismatch"
  | "history_policy_mismatch"
  | "payload_too_large";

export interface PublicRoomValidationIssue {
  readonly code: PublicRoomValidationIssueCode;
  readonly path: readonly (string | number)[];
  readonly fieldPath: string;
  readonly message: string;
}

export type PublicRoomValidationResult<T> =
  | {
      readonly success: true;
      readonly data: T;
    }
  | {
      readonly success: false;
      readonly issues: readonly PublicRoomValidationIssue[];
    };

class IssueCollector {
  readonly issues: PublicRoomValidationIssue[] = [];

  add(
    code: PublicRoomValidationIssueCode,
    path: readonly (string | number)[],
    message: string,
  ): void {
    if (this.issues.length >= MAX_VALIDATION_ISSUES) {
      return;
    }
    this.issues.push({
      code,
      path: [...path],
      fieldPath: fieldPath(path),
      message,
    });
  }
}

function fieldPath(path: readonly (string | number)[]): string {
  return path.reduce<string>(
    (result, segment) =>
      typeof segment === "number"
        ? `${result}[${segment}]`
        : result
          ? `${result}.${segment}`
          : segment,
    "",
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return false;
  }
  const prototype = Object.getPrototypeOf(value);
  return prototype === OBJECT_PROTOTYPE || prototype === null;
}

function hasOwn(value: object, key: string): boolean {
  return Object.hasOwn(value, key);
}

function rejectUnknownKeys(
  value: Record<string, unknown>,
  allowed: readonly string[],
  path: readonly (string | number)[],
  collector: IssueCollector,
): void {
  const allowlist = new Set(allowed);
  for (const key of Object.keys(value)) {
    if (!allowlist.has(key)) {
      collector.add(
        "unknown_key",
        [...path, key],
        "공개 계약에서 허용하지 않는 필드입니다.",
      );
    }
  }
}

function stringValue(
  value: unknown,
  path: readonly (string | number)[],
  collector: IssueCollector,
  options: {
    readonly maximumLength: number;
    readonly pattern?: RegExp;
  },
): string | null {
  if (
    typeof value !== "string" ||
    value.length === 0 ||
    value.length > options.maximumLength ||
    value !== value.trim() ||
    (options.pattern !== undefined && !options.pattern.test(value))
  ) {
    collector.add(
      "invalid_value",
      path,
      "공개 문자열 값이 정규형 또는 길이 제한을 충족하지 않습니다.",
    );
    return null;
  }
  return value;
}

function picklistValue<T extends string>(
  value: unknown,
  values: readonly T[],
  path: readonly (string | number)[],
  collector: IssueCollector,
): T | null {
  if (typeof value !== "string" || !values.includes(value as T)) {
    collector.add(
      "invalid_value",
      path,
      `허용 값은 ${values.join(", ")}입니다.`,
    );
    return null;
  }
  return value as T;
}

function readPolicy(
  input: unknown,
  path: readonly (string | number)[],
  collector: IssueCollector,
): ShareVisibilityPolicyV1 | null {
  if (!isRecord(input)) {
    collector.add("invalid_type", path, "공개 범위는 객체여야 합니다.");
    return null;
  }
  rejectUnknownKeys(input, POLICY_KEYS, path, collector);
  const identity = picklistValue(
    input.identity,
    SHARE_IDENTITY_VISIBILITY_VALUES,
    [...path, "identity"],
    collector,
  );
  const performance = picklistValue(
    input.performance,
    SHARE_PERFORMANCE_VISIBILITY_VALUES,
    [...path, "performance"],
    collector,
  );
  const allocation = picklistValue(
    input.allocation,
    SHARE_ALLOCATION_VISIBILITY_VALUES,
    [...path, "allocation"],
    collector,
  );
  const history = picklistValue(
    input.history,
    SHARE_HISTORY_VISIBILITY_VALUES,
    [...path, "history"],
    collector,
  );

  return identity && performance && allocation && history
    ? { identity, performance, allocation, history }
    : null;
}

export function validateShareVisibilityPolicyV1(
  input: unknown,
): PublicRoomValidationResult<ShareVisibilityPolicyV1> {
  const collector = new IssueCollector();
  const data = readPolicy(input, [], collector);
  return data && collector.issues.length === 0
    ? { success: true, data }
    : { success: false, issues: collector.issues };
}

function readRoom(
  input: unknown,
  path: readonly (string | number)[],
  collector: IssueCollector,
): PublicRoomStateV1 | null {
  if (!isRecord(input)) {
    collector.add("invalid_type", path, "공개 방 상태는 객체여야 합니다.");
    return null;
  }
  rejectUnknownKeys(input, ROOM_KEYS, path, collector);
  const weather = picklistValue(
    input.weather,
    PUBLIC_ROOM_WEATHER_VALUES,
    [...path, "weather"],
    collector,
  );
  return weather ? { weather } : null;
}

function readAppearance(
  input: unknown,
  path: readonly (string | number)[],
  collector: IssueCollector,
): PublicPetAppearanceV1 | null {
  if (!isRecord(input)) {
    collector.add("invalid_type", path, "공개 펫 외형은 객체여야 합니다.");
    return null;
  }
  rejectUnknownKeys(input, APPEARANCE_KEYS, path, collector);
  const paletteId = picklistValue(
    input.paletteId,
    PUBLIC_PET_PALETTE_VALUES,
    [...path, "paletteId"],
    collector,
  );
  const patternId = picklistValue(
    input.patternId,
    PUBLIC_PET_PATTERN_VALUES,
    [...path, "patternId"],
    collector,
  );
  const hatId =
    input.hatId === undefined
      ? undefined
      : picklistValue(
          input.hatId,
          PUBLIC_PET_HAT_VALUES,
          [...path, "hatId"],
          collector,
        );
  if (!paletteId || !patternId || hatId === null) {
    return null;
  }
  return {
    paletteId,
    patternId,
    ...(hatId === undefined ? {} : { hatId }),
  };
}

function readPerformance(
  input: unknown,
  policy: ShareVisibilityPolicyV1,
  path: readonly (string | number)[],
  collector: IssueCollector,
): PublicPerformanceSignalV1 | null {
  if (!isRecord(input)) {
    collector.add("invalid_type", path, "공개 등락 신호는 객체여야 합니다.");
    return null;
  }
  rejectUnknownKeys(input, PERFORMANCE_KEYS, path, collector);
  const direction = picklistValue(
    input.direction,
    PUBLIC_PERFORMANCE_DIRECTION_VALUES,
    [...path, "direction"],
    collector,
  );
  if (!direction) {
    return null;
  }

  if (policy.performance === "hidden") {
    collector.add(
      "privacy_policy_mismatch",
      path,
      "등락 숨김 정책에서는 등락 신호를 전송할 수 없습니다.",
    );
    return null;
  }

  if (policy.performance === "direction") {
    if (hasOwn(input, "roundedPercent")) {
      collector.add(
        "privacy_policy_mismatch",
        [...path, "roundedPercent"],
        "방향 공개 정책에서는 반올림 수익률을 전송할 수 없습니다.",
      );
      return null;
    }
    return { direction };
  }

  const rounded = input.roundedPercent;
  if (direction === "unknown") {
    if (hasOwn(input, "roundedPercent")) {
      collector.add(
        "invalid_value",
        [...path, "roundedPercent"],
        "알 수 없는 등락에는 숫자를 넣지 않습니다.",
      );
      return null;
    }
    return { direction };
  }
  if (
    typeof rounded !== "number" ||
    !Number.isInteger(rounded) ||
    rounded < -100 ||
    rounded > 100 ||
    (direction === "flat" && rounded !== 0) ||
    (direction === "up" && rounded <= 0) ||
    (direction === "down" && rounded >= 0)
  ) {
    collector.add(
      "invalid_value",
      [...path, "roundedPercent"],
      "반올림 수익률은 방향과 일치하는 -100~100 정수여야 합니다.",
    );
    return null;
  }
  return { direction, roundedPercent: rounded };
}

function readAllocation(
  input: unknown,
  policy: ShareVisibilityPolicyV1,
  path: readonly (string | number)[],
  collector: IssueCollector,
): PublicAllocationBucketV1 | null {
  if (policy.allocation === "hidden") {
    collector.add(
      "privacy_policy_mismatch",
      path,
      "비중 숨김 정책에서는 비중 구간을 전송할 수 없습니다.",
    );
    return null;
  }
  return picklistValue(
    input,
    PUBLIC_ALLOCATION_BUCKET_VALUES,
    path,
    collector,
  );
}

function sameAppearance(
  left: PublicPetAppearanceV1,
  right: PublicPetAppearanceV1,
): boolean {
  return (
    left.paletteId === right.paletteId &&
    left.patternId === right.patternId &&
    left.hatId === right.hatId
  );
}

function readPet(
  input: unknown,
  policy: ShareVisibilityPolicyV1,
  publicSeed: string,
  path: readonly (string | number)[],
  collector: IssueCollector,
): PublicRoomPetV1 | null {
  if (!isRecord(input)) {
    collector.add("invalid_type", path, "공개 펫은 객체여야 합니다.");
    return null;
  }
  rejectUnknownKeys(input, PET_KEYS, path, collector);
  const id = stringValue(input.id, [...path, "id"], collector, {
    maximumLength: 28,
    pattern: PUBLIC_ROOM_PET_ID_PATTERN,
  });
  const appearance = readAppearance(
    input.appearance,
    [...path, "appearance"],
    collector,
  );
  if (!id || !appearance) {
    return null;
  }

  const result: {
    id: string;
    appearance: PublicPetAppearanceV1;
    ticker?: string;
    name?: string;
    logoKey?: string;
    performance?: PublicPerformanceSignalV1;
    allocationBucket?: PublicAllocationBucketV1;
  } = { id, appearance };

  if (policy.identity === "hidden") {
    for (const field of ["ticker", "name", "logoKey"] as const) {
      if (hasOwn(input, field)) {
        collector.add(
          "privacy_policy_mismatch",
          [...path, field],
          "종목 숨김 정책에서는 식별 정보를 전송할 수 없습니다.",
        );
      }
    }
    const neutral = neutralAppearanceForPublicPetV1(publicSeed, id);
    if (!sameAppearance(appearance, neutral)) {
      collector.add(
        "privacy_policy_mismatch",
        [...path, "appearance"],
        "종목 숨김 정책에서는 공유 세션용 중립 외형만 사용할 수 있습니다.",
      );
    }
  } else {
    if (input.ticker !== undefined) {
      const ticker = stringValue(
        input.ticker,
        [...path, "ticker"],
        collector,
        { maximumLength: 20, pattern: PUBLIC_ROOM_TICKER_PATTERN },
      );
      if (ticker) result.ticker = ticker;
    }
    if (input.name !== undefined) {
      const name = stringValue(
        input.name,
        [...path, "name"],
        collector,
        { maximumLength: MAX_PUBLIC_PET_NAME_LENGTH },
      );
      if (name) result.name = name;
    }
    if (input.logoKey !== undefined) {
      const logoKey = stringValue(
        input.logoKey,
        [...path, "logoKey"],
        collector,
        { maximumLength: MAX_PUBLIC_LOGO_KEY_LENGTH },
      );
      if (logoKey) result.logoKey = logoKey;
    }
    if (
      result.ticker === undefined &&
      result.name === undefined
    ) {
      collector.add(
        "invalid_value",
        path,
        "종목 공개 펫에는 티커 또는 이름이 필요합니다.",
      );
    }
  }

  if (input.performance !== undefined) {
    const performance = readPerformance(
      input.performance,
      policy,
      [...path, "performance"],
      collector,
    );
    if (performance) result.performance = performance;
  }
  if (input.allocationBucket !== undefined) {
    const allocationBucket = readAllocation(
      input.allocationBucket,
      policy,
      [...path, "allocationBucket"],
      collector,
    );
    if (allocationBucket) result.allocationBucket = allocationBucket;
  }
  return result;
}

function readHistoryPet(
  input: unknown,
  policy: ShareVisibilityPolicyV1,
  currentPetIds: ReadonlySet<string>,
  path: readonly (string | number)[],
  collector: IssueCollector,
): PublicRoomHistoryPetV1 | null {
  if (!isRecord(input)) {
    collector.add("invalid_type", path, "과거 펫 신호는 객체여야 합니다.");
    return null;
  }
  rejectUnknownKeys(input, HISTORY_PET_KEYS, path, collector);
  const id = stringValue(input.id, [...path, "id"], collector, {
    maximumLength: 28,
    pattern: PUBLIC_ROOM_PET_ID_PATTERN,
  });
  if (!id) return null;
  if (!currentPetIds.has(id)) {
    collector.add(
      "invalid_value",
      [...path, "id"],
      "과거 신호는 현재 공개 펫 ID만 참조할 수 있습니다.",
    );
  }
  const result: {
    id: string;
    performance?: PublicPerformanceSignalV1;
    allocationBucket?: PublicAllocationBucketV1;
  } = { id };
  if (input.performance !== undefined) {
    const performance = readPerformance(
      input.performance,
      policy,
      [...path, "performance"],
      collector,
    );
    if (performance) result.performance = performance;
  }
  if (input.allocationBucket !== undefined) {
    const allocationBucket = readAllocation(
      input.allocationBucket,
      policy,
      [...path, "allocationBucket"],
      collector,
    );
    if (allocationBucket) result.allocationBucket = allocationBucket;
  }
  return result;
}

function historyDayLimit(
  policy: Exclude<ShareHistoryVisibilityV1, "none">,
): number {
  switch (policy) {
    case "7d":
      return 7;
    case "30d":
      return 30;
    case "90d":
      return 90;
  }
}

function calendarDayNumber(value: string): number | null {
  if (!isIsoCalendarDate(value)) return null;
  const [year, month, day] = value.split("-").map(Number);
  const date = new Date(0);
  date.setUTCHours(0, 0, 0, 0);
  date.setUTCFullYear(year!, month! - 1, day!);
  return Math.floor(date.getTime() / 86_400_000);
}

function readHistoryPoint(
  input: unknown,
  policy: ShareVisibilityPolicyV1,
  currentPetIds: ReadonlySet<string>,
  path: readonly (string | number)[],
  collector: IssueCollector,
): PublicRoomHistoryPointV1 | null {
  if (!isRecord(input)) {
    collector.add("invalid_type", path, "과거 방 기록은 객체여야 합니다.");
    return null;
  }
  rejectUnknownKeys(input, HISTORY_POINT_KEYS, path, collector);
  const marketDate =
    typeof input.marketDate === "string" &&
    isIsoCalendarDate(input.marketDate)
      ? input.marketDate
      : null;
  if (!marketDate) {
    collector.add(
      "invalid_value",
      [...path, "marketDate"],
      "실제로 존재하는 YYYY-MM-DD 날짜여야 합니다.",
    );
  }
  const room = readRoom(input.room, [...path, "room"], collector);
  if (!Array.isArray(input.pets)) {
    collector.add(
      "invalid_type",
      [...path, "pets"],
      "과거 펫 신호는 배열이어야 합니다.",
    );
    return null;
  }
  if (input.pets.length > MAX_PUBLIC_ROOM_PETS) {
    collector.add(
      "too_many_items",
      [...path, "pets"],
      `과거 펫 신호는 ${MAX_PUBLIC_ROOM_PETS}개 이하여야 합니다.`,
    );
    return null;
  }
  const pets = input.pets
    .map((pet, index) =>
      readHistoryPet(
        pet,
        policy,
        currentPetIds,
        [...path, "pets", index],
        collector,
      ),
    )
    .filter((pet): pet is PublicRoomHistoryPetV1 => pet !== null);
  const ids = new Set<string>();
  for (const [index, pet] of pets.entries()) {
    if (ids.has(pet.id)) {
      collector.add(
        "duplicate_value",
        [...path, "pets", index, "id"],
        "한 날짜에 같은 공개 펫 ID를 중복할 수 없습니다.",
      );
    }
    ids.add(pet.id);
  }
  return marketDate && room ? { marketDate, room, pets } : null;
}

function readHistory(
  input: unknown,
  policy: ShareVisibilityPolicyV1,
  currentPetIds: ReadonlySet<string>,
  path: readonly (string | number)[],
  collector: IssueCollector,
): PublicRoomHistoryV1 | null {
  if (!isRecord(input)) {
    collector.add("invalid_type", path, "공개 과거 기록은 객체여야 합니다.");
    return null;
  }
  rejectUnknownKeys(input, HISTORY_KEYS, path, collector);
  const range = picklistValue(
    input.range,
    ["7d", "30d", "90d"] as const,
    [...path, "range"],
    collector,
  );
  if (!range) return null;
  if (policy.history === "none" || range !== policy.history) {
    collector.add(
      "history_policy_mismatch",
      [...path, "range"],
      "과거 기록 범위가 공개 정책과 일치하지 않습니다.",
    );
  }
  if (!Array.isArray(input.points)) {
    collector.add(
      "invalid_type",
      [...path, "points"],
      "과거 기록은 배열이어야 합니다.",
    );
    return null;
  }
  const maximumPoints = Math.min(
    historyDayLimit(range),
    MAX_PUBLIC_ROOM_HISTORY_POINTS,
  );
  if (input.points.length > maximumPoints) {
    collector.add(
      "too_many_items",
      [...path, "points"],
      `${range} 공개 기록은 ${maximumPoints}개 이하여야 합니다.`,
    );
    return null;
  }
  const points = input.points
    .map((point, index) =>
      readHistoryPoint(
        point,
        policy,
        currentPetIds,
        [...path, "points", index],
        collector,
      ),
    )
    .filter((point): point is PublicRoomHistoryPointV1 => point !== null);
  for (let index = 1; index < points.length; index += 1) {
    if (points[index - 1]!.marketDate >= points[index]!.marketDate) {
      collector.add(
        points[index - 1]!.marketDate === points[index]!.marketDate
          ? "duplicate_value"
          : "invalid_value",
        [...path, "points", index, "marketDate"],
        "과거 기록 날짜는 중복 없이 오름차순이어야 합니다.",
      );
    }
  }
  if (points.length > 1) {
    const firstDay = calendarDayNumber(points[0]!.marketDate);
    const lastDay = calendarDayNumber(points.at(-1)!.marketDate);
    if (
      firstDay !== null &&
      lastDay !== null &&
      lastDay - firstDay >= historyDayLimit(range)
    ) {
      collector.add(
        "history_policy_mismatch",
        [...path, "points"],
        `${range} 공개 기록은 선택한 달력 기간 안에 있어야 합니다.`,
      );
    }
  }
  return { range, points };
}

function compareCanonicalKeys(left: string, right: string): number {
  return left < right ? -1 : left > right ? 1 : 0;
}

function canonicalJsonValue(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(canonicalJsonValue);
  }
  if (!isRecord(value)) {
    return value;
  }
  return Object.fromEntries(
    Object.keys(value)
      .sort(compareCanonicalKeys)
      .filter((key) => value[key] !== undefined)
      .map((key) => [key, canonicalJsonValue(value[key])]),
  );
}

function canonicalJson(data: PublicRoomSnapshotV1): string {
  return JSON.stringify(canonicalJsonValue(data));
}

export function validatePublicRoomSnapshotV1(
  input: unknown,
): PublicRoomValidationResult<PublicRoomSnapshotV1> {
  const collector = new IssueCollector();
  if (!isRecord(input)) {
    collector.add("invalid_type", [], "공개 방 스냅샷은 객체여야 합니다.");
    return { success: false, issues: collector.issues };
  }
  rejectUnknownKeys(input, SNAPSHOT_KEYS, [], collector);
  if (input.schemaVersion !== PUBLIC_ROOM_SNAPSHOT_SCHEMA_VERSION) {
    collector.add(
      typeof input.schemaVersion === "number"
        ? "unsupported_version"
        : "invalid_type",
      ["schemaVersion"],
      "지원하지 않는 공개 방 스키마 버전입니다.",
    );
  }
  const rendererVersion = stringValue(
    input.rendererVersion,
    ["rendererVersion"],
    collector,
    { maximumLength: MAX_PUBLIC_RENDERER_VERSION_LENGTH },
  );
  const publicSeed = stringValue(
    input.publicSeed,
    ["publicSeed"],
    collector,
    { maximumLength: 128, pattern: PUBLIC_ROOM_SEED_PATTERN },
  );
  const policy = readPolicy(input.visibility, ["visibility"], collector);
  const room = readRoom(input.room, ["room"], collector);
  if (!Array.isArray(input.pets)) {
    collector.add("invalid_type", ["pets"], "공개 펫은 배열이어야 합니다.");
    return { success: false, issues: collector.issues };
  }
  if (input.pets.length > MAX_PUBLIC_ROOM_PETS) {
    collector.add(
      "too_many_items",
      ["pets"],
      `공개 펫은 ${MAX_PUBLIC_ROOM_PETS}개 이하여야 합니다.`,
    );
    return { success: false, issues: collector.issues };
  }

  const pets =
    policy && publicSeed
      ? input.pets
          .map((pet, index) =>
            readPet(
              pet,
              policy,
              publicSeed,
              ["pets", index],
              collector,
            ),
          )
          .filter((pet): pet is PublicRoomPetV1 => pet !== null)
      : [];
  const currentPetIds = new Set<string>();
  for (const [index, pet] of pets.entries()) {
    if (currentPetIds.has(pet.id)) {
      collector.add(
        "duplicate_value",
        ["pets", index, "id"],
        "공개 펫 ID는 중복될 수 없습니다.",
      );
    }
    currentPetIds.add(pet.id);
  }

  let history: PublicRoomHistoryV1 | undefined;
  if (policy?.history === "none") {
    if (hasOwn(input, "history")) {
      collector.add(
        "history_policy_mismatch",
        ["history"],
        "과거 기록 없음 정책에서는 history를 전송할 수 없습니다.",
      );
    }
  } else if (policy) {
    if (!hasOwn(input, "history")) {
      collector.add(
        "history_policy_mismatch",
        ["history"],
        "선택한 기간의 공개 기록이 필요합니다.",
      );
    } else {
      history =
        readHistory(
          input.history,
          policy,
          currentPetIds,
          ["history"],
          collector,
        ) ?? undefined;
    }
  }

  if (
    input.schemaVersion !== PUBLIC_ROOM_SNAPSHOT_SCHEMA_VERSION ||
    !rendererVersion ||
    !publicSeed ||
    !policy ||
    !room ||
    collector.issues.length > 0
  ) {
    return { success: false, issues: collector.issues };
  }
  const data: PublicRoomSnapshotV1 = {
    schemaVersion: PUBLIC_ROOM_SNAPSHOT_SCHEMA_VERSION,
    rendererVersion,
    publicSeed,
    visibility: policy,
    room,
    pets,
    ...(history ? { history } : {}),
  };
  if (
    new TextEncoder().encode(canonicalJson(data)).byteLength >
    MAX_PUBLIC_ROOM_SNAPSHOT_BYTES
  ) {
    collector.add(
      "payload_too_large",
      [],
      `공개 방 스냅샷은 ${MAX_PUBLIC_ROOM_SNAPSHOT_BYTES}바이트 이하여야 합니다.`,
    );
    return { success: false, issues: collector.issues };
  }
  return { success: true, data };
}

/**
 * Alias naming the validator's second responsibility: successful data is a
 * fresh allowlisted object, never the caller's possibly polluted reference.
 */
export const canonicalizePublicRoomSnapshotV1 =
  validatePublicRoomSnapshotV1;

export function canonicalPublicRoomSnapshotJsonV1(
  input: unknown,
): PublicRoomValidationResult<string> {
  const validated = validatePublicRoomSnapshotV1(input);
  return validated.success
    ? { success: true, data: canonicalJson(validated.data) }
    : validated;
}
